import { toTimeseriesNumeric as toNumeric } from "./timeseriesChart";

function getDisplayText(value, fallback = "-") {
  const text = String(value ?? "").trim();
  return text || fallback;
}

function parseDateValue(value) {
  if (!value) return null;

  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) {
    return direct;
  }

  const match = String(value).match(/(\d{4})\D+(\d{1,2})\D+(\d{1,2})/);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  const parsed = new Date(year, month, day);

  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function compareByShootingDate(a, b) {
  const aDate = parseDateValue(a?.shooting_date || a?.uploaded_at);
  const bDate = parseDateValue(b?.shooting_date || b?.uploaded_at);

  if (aDate && bDate) return aDate - bDate;
  if (aDate) return -1;
  if (bDate) return 1;

  return String(a?.file_name ?? "").localeCompare(String(b?.file_name ?? ""));
}

function getUploadId(item) {
  return String(item?.upload_id ?? item?.id ?? "");
}

function getThreePhaseDifferenceValue(clickPoints) {
  const values = Array.isArray(clickPoints)
    ? clickPoints
        .map((point) => toNumeric(point?.value))
        .filter((value) => value != null)
    : [];

  if (values.length === 0) {
    return null;
  }

  return Math.max(...values) - Math.min(...values);
}

function getAnalysisDateLabel(item) {
  return getDisplayText(
    item?.shooting_date || item?.uploaded_at,
    item?.file_name,
  );
}

function getDirectionText(difference, t) {
  if (difference > 0) {
    return t("imageFileIndex.timeseries.opinion.comprehensive.direction.rise", {
      defaultValue: "상승",
    });
  }

  if (difference < 0) {
    return t("imageFileIndex.timeseries.opinion.comprehensive.direction.fall", {
      defaultValue: "하락",
    });
  }

  return t("imageFileIndex.timeseries.opinion.comprehensive.direction.steady", {
    defaultValue: "유지",
  });
}

function formatNumber(value, digits = 1) {
  return Number(value).toFixed(digits);
}

function summarizeMetricChange(items, getValue) {
  const points = items
    .map((item) => ({ item, value: getValue(item) }))
    .filter((point) => point.value != null);

  if (points.length < 2) {
    return null;
  }

  const startPoint = points[0];
  const endPoint = points[points.length - 1];
  const totalDifference = endPoint.value - startPoint.value;
  let totalStepDifference = 0;
  let stepCount = 0;
  let largestChange = null;

  for (let index = 1; index < points.length; index += 1) {
    const previousPoint = points[index - 1];
    const currentPoint = points[index];
    const difference = currentPoint.value - previousPoint.value;
    const absDifference = Math.abs(difference);

    totalStepDifference += absDifference;
    stepCount += 1;

    if (!largestChange || absDifference > largestChange.absDifference) {
      largestChange = {
        previousItem: previousPoint.item,
        currentItem: currentPoint.item,
        difference,
        absDifference,
      };
    }
  }

  return {
    startItem: startPoint.item,
    endItem: endPoint.item,
    startValue: startPoint.value,
    endValue: endPoint.value,
    totalDifference,
    totalAbsDifference: Math.abs(totalDifference),
    averageStepDifference:
      stepCount > 0 ? totalStepDifference / stepCount : null,
    largestChange,
  };
}

function buildMetricParagraph(labelKey, summary, unit, t) {
  const label = t(labelKey, { defaultValue: labelKey });

  if (!summary || !summary.largestChange || summary.averageStepDifference == null) {
    return t("imageFileIndex.timeseries.opinion.comprehensive.insufficientData", {
      label,
      defaultValue:
        "{{label}} 변화 추이를 계산하기에 충분한 데이터가 없어 분석에서 제외되었습니다.",
    });
  }

  const totalDirection = getDirectionText(summary.totalDifference, t);
  const largestDirection = getDirectionText(summary.largestChange.difference, t);

  return [
    t("imageFileIndex.timeseries.opinion.comprehensive.metricSummary", {
      label,
      startValue: formatNumber(summary.startValue),
      endValue: formatNumber(summary.endValue),
      totalChange: formatNumber(summary.totalAbsDifference),
      direction: totalDirection,
      unit,
      defaultValue:
        "{{label}} {{startValue}}{{unit}}에서 {{endValue}}{{unit}}로 {{totalChange}}{{unit}} {{direction}}했습니다.",
    }),
    t("imageFileIndex.timeseries.opinion.comprehensive.metricDetail", {
      averageChange: formatNumber(summary.averageStepDifference),
      maxChange: formatNumber(summary.largestChange.absDifference),
      direction: largestDirection,
      unit,
      defaultValue:
        "데이터 간 평균 변화폭은 {{averageChange}}{{unit}}였으며, 가장 큰 변화는 {{maxChange}}{{unit}} {{direction}}이었습니다.",
    }),
  ].join(" ");
}

function formatComprehensiveOpinionText(paragraphs) {
  const filteredParagraphs = paragraphs.filter((paragraph) =>
    String(paragraph ?? "").trim(),
  );
  const [periodSummary, ultrasonicParagraph, ...restParagraphs] =
    filteredParagraphs;
  const ultrasonicText = String(ultrasonicParagraph ?? "").trim();
  const ultrasonicMatch = ultrasonicText.match(
    /^(.+?(?:[!?。！？]|\.(?=\s|$)))\s*(.*)$/u,
  );
  const ultrasonicSentences =
    ultrasonicMatch && ultrasonicMatch[2]
      ? [ultrasonicMatch[1], ultrasonicMatch[2]]
      : [ultrasonicText];

  return [
    [periodSummary, ultrasonicSentences[0]].filter(Boolean).join(" "),
    ...ultrasonicSentences.slice(1),
    ...restParagraphs,
  ]
    .filter((paragraph) => String(paragraph ?? "").trim())
    .join("\n\n")
    .trim();
}

/*
 * HELPER: 현재 파일을 시계열 분석 시작점으로 보고, 최신 선택 데이터까지의 종합의견 문장을 생성한다.
 * - 초음파(avg_decibel), 검출온도(thermal_center), 3상 비교 온도차를 같은 형식으로 요약한다.
 * - 현재 파일보다 과거 데이터가 함께 선택되어 있어도 현재 파일 이전 구간은 제외한다.
 */
export function buildTimeseriesComprehensiveOpinion({
  files = [],
  startUploadId = "",
  t = (key, options = {}) => options.defaultValue ?? key,
}) {
  const sortedFiles = [...files].sort(compareByShootingDate);
  const startIndex = sortedFiles.findIndex(
    (item) => getUploadId(item) === String(startUploadId ?? ""),
  );
  const filesFromCurrent =
    startIndex >= 0 ? sortedFiles.slice(startIndex) : sortedFiles;
  const analysisFiles =
    filesFromCurrent.length >= 2 ? filesFromCurrent : sortedFiles;

  if (analysisFiles.length === 0) {
    return "";
  }

  const periodStartLabel = getAnalysisDateLabel(analysisFiles[0]);
  const periodEndLabel = getAnalysisDateLabel(
    analysisFiles[analysisFiles.length - 1],
  );
  const ultrasonicSummary = summarizeMetricChange(
    analysisFiles,
    (item) => toNumeric(item?.avg_decibel),
  );
  const temperatureSummary = summarizeMetricChange(
    analysisFiles,
    (item) => toNumeric(item?.thermal_center),
  );
  const threePhaseSummary = summarizeMetricChange(
    analysisFiles,
    (item) => getThreePhaseDifferenceValue(item?.click_points),
  );

  return formatComprehensiveOpinionText([
    t("imageFileIndex.timeseries.opinion.comprehensive.periodSummary", {
      start: periodStartLabel,
      end: periodEndLabel,
      defaultValue: "{{start}}부터 {{end}}까지의 시계열 데이터를 분석한 결과,",
    }),
    buildMetricParagraph(
      "imageFileIndex.timeseries.opinion.comprehensive.labels.ultrasonicAverage",
      ultrasonicSummary,
      " dB",
      t,
    ),
    buildMetricParagraph(
      "imageFileIndex.timeseries.opinion.comprehensive.labels.detectedTemperature",
      temperatureSummary,
      "°C",
      t,
    ),
    buildMetricParagraph(
      "imageFileIndex.timeseries.opinion.comprehensive.labels.threePhaseDifference",
      threePhaseSummary,
      "°C",
      t,
    ),
  ]);
}
